import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ShoppingBasketRoundedIcon from "@mui/icons-material/ShoppingBasketRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { getApiError } from "../api/errors";
import {
  getReportCoverage,
  getSalesReportInsights,
  getSalesReportSummary,
} from "../api/reports";
import PageHeader from "../components/PageHeader";
import { EmptyPanel, ErrorPanel, LoadingPanel } from "../components/PageState";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";
import { formatDate, formatMoney, titleCase } from "../utils/format";

function localDateValue() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

function todayValue() {
  return localDateValue();
}

function monthValue() {
  return localDateValue().slice(0, 7);
}

function MetricCard({ label, value, helper, icon: Icon, tone = "blue", badge = null }) {
  const tones = {
    blue: { background: "#eff6ff", foreground: "#2563eb" },
    green: { background: "#f0fdf4", foreground: "#16a34a" },
    amber: { background: "#fff7ed", foreground: "#d97706" },
    violet: { background: "#f5f3ff", foreground: "#7c3aed" },
  };
  const colors = tones[tone] || tones.blue;

  return (
    <Card sx={{ height: "100%", borderRadius: 4 }}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" gap={2}>
          <Box minWidth={0}>
            <Typography variant="body2" color="text.secondary" fontWeight={800}>
              {label}
            </Typography>
            <Typography variant="h4" sx={{ mt: 0.8 }}>
              {value}
            </Typography>
            {badge && <Box sx={{ mt: 1 }}>{badge}</Box>}
          </Box>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: 3,
              bgcolor: colors.background,
              color: colors.foreground,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <Icon />
          </Box>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
          {helper}
        </Typography>
      </CardContent>
    </Card>
  );
}

function CapabilityChip({ enabled, label }) {
  const { t } = useTranslation();
  return (
    <Chip
      size="small"
      label={`${label}: ${t(enabled ? "reports.available" : "reports.unavailable")}`}
      color={enabled ? "success" : "default"}
      variant={enabled ? "filled" : "outlined"}
      sx={{ fontWeight: 750 }}
    />
  );
}

function SalesTrendChart({ points = [] }) {
  const { t } = useTranslation();
  const width = 760;
  const height = 280;
  const left = 62;
  const right = 20;
  const top = 24;
  const bottom = 48;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const values = points.map((point) => Number(point.net_sales || 0));
  const minValue = Math.min(0, ...values);
  const maxValue = Math.max(0, ...values);
  const spread = Math.max(maxValue - minValue, 1);
  const x = (index) => (
    points.length <= 1
      ? left + chartWidth / 2
      : left + (index / (points.length - 1)) * chartWidth
  );
  const y = (value) => top + ((maxValue - value) / spread) * chartHeight;
  const path = points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${x(index)} ${y(Number(point.net_sales || 0))}`)
    .join(" ");
  const yTicks = Array.from({ length: 5 }, (_, index) => maxValue - (spread * index) / 4);
  const xStep = Math.max(1, Math.ceil(points.length / 6));
  const hasActivity = points.some((point) => (
    Number(point.transaction_count || 0) > 0 || Number(point.refunds || 0) > 0
  ));

  if (!points.length || !hasActivity) {
    return (
      <Box sx={{ minHeight: 260, display: "grid", placeItems: "center", textAlign: "center", px: 3 }}>
        <Box>
          <InsightsRoundedIcon color="disabled" sx={{ fontSize: 42 }} />
          <Typography fontWeight={850} sx={{ mt: 1 }}>{t("reports.noActivity")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("reports.trendHelp")}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Box component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: "100%", minWidth: 620, display: "block" }}>
        {yTicks.map((tick, index) => {
          const tickY = top + (chartHeight * index) / 4;
          return (
            <g key={`${tick}-${index}`}>
              <line x1={left} x2={width - right} y1={tickY} y2={tickY} stroke="#e5e7eb" strokeWidth="1" />
              <text x={left - 10} y={tickY + 4} textAnchor="end" fontSize="12" fill="#64748b">
                {formatMoney(tick, "USD")}
              </text>
            </g>
          );
        })}
        <line x1={left} x2={width - right} y1={y(0)} y2={y(0)} stroke="#94a3b8" strokeWidth="1" />
        <path d={path} fill="none" stroke="#2563eb" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((point, index) => {
          const showLabel = index === 0 || index === points.length - 1 || index % xStep === 0;
          return (
            <g key={`${point.bucket_start}-${index}`}>
              <circle
                cx={x(index)}
                cy={y(Number(point.net_sales || 0))}
                r={Number(point.transaction_count || 0) > 0 ? 6 : 3.5}
                fill="#2563eb"
              >
                <title>{t("reports.chartPoint", { label: point.label, amount: formatMoney(point.net_sales, "USD"), count: point.transaction_count })}</title>
              </circle>
              {Number(point.transaction_count || 0) > 0 && (
                <text
                  x={x(index)}
                  y={Math.max(top + 12, y(Number(point.net_sales || 0)) - 12)}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="700"
                  fill="#1d4ed8"
                >
                  {formatMoney(point.net_sales, "USD")}
                </text>
              )}
              {showLabel && (
                <text x={x(index)} y={height - 18} textAnchor="middle" fontSize="12" fill="#64748b">
                  {point.label}
                </text>
              )}
            </g>
          );
        })}
      </Box>
    </Box>
  );
}

function RankedBars({ items, labelKey, valueKey, emptyLabel, valueFormatter = (value) => value }) {
  const maxValue = Math.max(0, ...items.map((item) => Math.max(0, Number(item[valueKey] || 0))));

  if (!items.length) {
    return <Typography color="text.secondary">{emptyLabel}</Typography>;
  }

  return (
    <Stack spacing={1.8}>
      {items.map((item) => {
        const raw = Number(item[valueKey] || 0);
        const percentage = maxValue > 0 ? Math.max(3, (Math.max(0, raw) / maxValue) * 100) : 0;
        return (
          <Box key={`${item[labelKey]}-${raw}`}>
            <Stack direction="row" justifyContent="space-between" gap={2} sx={{ mb: 0.7 }}>
              <Typography fontWeight={800} noWrap>{item[labelKey]}</Typography>
              <Typography fontWeight={850}>{valueFormatter(raw)}</Typography>
            </Stack>
            <Box sx={{ height: 9, bgcolor: "#eef2f7", borderRadius: 999, overflow: "hidden" }}>
              <Box sx={{ width: `${percentage}%`, height: "100%", bgcolor: "primary.main", borderRadius: 999 }} />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

function PaymentBreakdown({ items = [] }) {
  const { t } = useTranslation();
  const total = items.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  if (!items.length) {
    return <Typography color="text.secondary">{t("reports.noPayments")}</Typography>;
  }

  return (
    <Stack spacing={1.6}>
      {items.map((item) => {
        const amount = Number(item.amount || 0);
        const share = total > 0 ? (amount / total) * 100 : 0;
        return (
          <Box key={item.method}>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <Box>
                <Typography fontWeight={850}>{titleCase(item.method)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("reports.paymentCount", { count: item.payment_count, percent: share.toFixed(1) })}
                </Typography>
              </Box>
              <Typography fontWeight={900}>{formatMoney(amount, "USD")}</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={Math.min(100, share)} sx={{ mt: 0.9, height: 8, borderRadius: 999 }} />
          </Box>
        );
      })}
    </Stack>
  );
}

function AIInsightsCard({ period, anchorDate, transactionCount }) {
  const { t } = useTranslation();
  const [question, setQuestion] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setAnalysis(null);
    setError("");
    setQuestion("");
  }, [period, anchorDate]);

  const analyze = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getSalesReportInsights({
        period,
        anchorDate,
        question: question.trim() || null,
      });
      setAnalysis(result);
    } catch (requestError) {
      setError(getApiError(requestError, t("reports.aiError")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ mt: 2.5, borderRadius: 4, border: "1px solid #dbeafe", overflow: "hidden" }}>
      <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2.5}>
          <Box sx={{ maxWidth: 760 }}>
            <Stack direction="row" spacing={1.1} alignItems="center">
              <AutoAwesomeRoundedIcon color="primary" />
              <Typography variant="h6" fontWeight={900}>{t("reports.aiAnalyst")}</Typography>
              <Chip size="small" label={t("reports.aiScope")} variant="outlined" color="primary" />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
              {t("reports.aiHelp")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeRoundedIcon />}
            onClick={analyze}
            disabled={loading || transactionCount <= 0}
            sx={{ alignSelf: { xs: "stretch", md: "flex-start" }, minWidth: 170 }}
          >
            {t(loading ? "reports.analyzing" : "reports.analyze")}
          </Button>
        </Stack>

        <TextField
          fullWidth
          size="small"
          label={t("reports.optionalQuestion")}
          placeholder={t("reports.questionExample")}
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          inputProps={{ maxLength: 500 }}
          disabled={loading || transactionCount <= 0}
          sx={{ mt: 2.2 }}
        />

        {transactionCount <= 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t("reports.aiUnavailable")}
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        {analysis && (
          <Box sx={{ mt: 2.5 }}>
            <Typography variant="h6" fontWeight={900}>{analysis.headline}</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.6 }}>{analysis.executive_summary}</Typography>
            {analysis.answer && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography fontWeight={850} sx={{ mb: 0.4 }}>{t("reports.answer")}</Typography>
                {analysis.answer}
              </Alert>
            )}
            <Grid container spacing={1.5} sx={{ mt: 0.6 }}>
              {(analysis.insights || []).map((item) => {
                const border = item.tone === "positive" ? "#bbf7d0" : item.tone === "attention" ? "#fed7aa" : "#dbeafe";
                const background = item.tone === "positive" ? "#f0fdf4" : item.tone === "attention" ? "#fff7ed" : "#f8fbff";
                return (
                  <Grid item xs={12} md={6} key={`${item.title}-${item.detail}`}>
                    <Box sx={{ height: "100%", p: 2, borderRadius: 3, border: `1px solid ${border}`, bgcolor: background }}>
                      <Typography fontWeight={900}>{item.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{item.detail}</Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default function Reports() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const [period, setPeriod] = useState("daily");
  const [dailyDate, setDailyDate] = useState(todayValue());
  const [monthlyDate, setMonthlyDate] = useState(monthValue());
  const [coverage, setCoverage] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const anchorDate = period === "daily" ? dailyDate : `${monthlyDate}-01`;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    if (!selectedStore?.public_id) {
      setCoverage(null);
      setSummary(null);
      setLoading(false);
      return;
    }
    try {
      const [coverageResult, summaryResult] = await Promise.all([
        getReportCoverage(),
        getSalesReportSummary({ period, anchorDate, topLimit: 10 }),
      ]);
      setCoverage(coverageResult);
      setSummary(summaryResult);
    } catch (requestError) {
      setError(getApiError(requestError, t("reports.loadError")));
    } finally {
      setLoading(false);
    }
  }, [anchorDate, period, selectedStore?.public_id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const change = Number(summary?.comparison?.net_sales_change || 0);
  const changePercent = summary?.comparison?.net_sales_change_percent;
  const comparisonLabel = useMemo(() => {
    if (!summary) return t("reports.noComparison");
    if (changePercent === null || changePercent === undefined) {
      return t(change === 0 ? "reports.noChange" : "reports.noBaseline");
    }
    const sign = Number(changePercent) > 0 ? "+" : "";
    return t("reports.vsPrevious", { percent: `${sign}${Number(changePercent).toFixed(2)}` });
  }, [change, changePercent, summary, t]);

  const comparisonChip = summary ? (
    <Chip
      size="small"
      icon={change >= 0 ? <TrendingUpRoundedIcon /> : <TrendingDownRoundedIcon />}
      color={change >= 0 ? "success" : "error"}
      variant="outlined"
      label={comparisonLabel}
      sx={{ fontWeight: 800 }}
    />
  ) : null;

  return (
    <MainLayout>
      <PageHeader
        eyebrow={t("reports.eyebrow")}
        title={t("nav.reports")}
        description={t("reports.subtitle", { store: selectedStore?.name || t("reports.thisStore") })}
        status={coverage ? {
          label: t(coverage.sales_records_available ? "reports.active" : "reports.waiting"),
          color: coverage.sales_records_available ? "success" : "warning",
          variant: "outlined",
        } : null}
        actions={(
          <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load}>
            {t("catalog.refresh")}
          </Button>
        )}
      />

      <ErrorPanel message={error} onRetry={load} />

      {loading ? (
        <LoadingPanel label={t("reports.loading")} />
      ) : (
        <>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", lg: "center" }}
            spacing={2}
            sx={{ mb: 2.5 }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel id="report-period-label">{t("reports.period")}</InputLabel>
                <Select
                  labelId="report-period-label"
                  label={t("reports.period")}
                  value={period}
                  onChange={(event) => setPeriod(event.target.value)}
                >
                  <MenuItem value="daily">{t("reports.daily")}</MenuItem>
                  <MenuItem value="monthly">{t("reports.monthly")}</MenuItem>
                </Select>
              </FormControl>
              {period === "daily" ? (
                <TextField
                  type="date"
                  size="small"
                  label={t("reports.day")}
                  value={dailyDate}
                  onChange={(event) => setDailyDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              ) : (
                <TextField
                  type="month"
                  size="small"
                  label={t("reports.month")}
                  value={monthlyDate}
                  onChange={(event) => setMonthlyDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            </Stack>

            {coverage && (
              <Stack direction="row" gap={0.8} flexWrap="wrap" useFlexGap>
                <CapabilityChip enabled={coverage.sales_supported} label={t("nav.sales")} />
                <CapabilityChip enabled={coverage.payment_records_available || coverage.payments_supported} label={t("sales.payments")} />
                <CapabilityChip enabled={coverage.returns_supported} label={t("sales.returns")} />
                {coverage.native_pos_sales_available && <Chip size="small" color="primary" label={t("sales.nativePos")} sx={{ fontWeight: 750 }} />}
                {coverage.imported_pos_sales_available && <Chip size="small" variant="outlined" label={t("sales.importedPos")} sx={{ fontWeight: 750 }} />}
              </Stack>
            )}
          </Stack>

          {!coverage?.sales_records_available && Number(summary?.operating_expenses || 0) === 0 ? (
            <EmptyPanel
              icon="📊"
              title={t("reports.ready")}
              description={coverage?.sales_supported
                ? t("reports.noSalesHelp")
                : t("reports.connectHelp")}
            />
          ) : (
            <>
              <Grid container spacing={2.25}>
                <Grid item xs={12} sm={6} lg={3}>
                  <MetricCard
                    label={t("reports.netSales")}
                    value={formatMoney(summary?.net_sales, "USD")}
                    helper={t("reports.grossBeforeRefunds", { amount: formatMoney(summary?.gross_sales, "USD") })}
                    icon={AttachMoneyRoundedIcon}
                    tone="green"
                    badge={comparisonChip}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <MetricCard
                    label={t("reports.transactions")}
                    value={Number(summary?.transaction_count || 0).toLocaleString()}
                    helper={t("reports.averageTicket", { amount: formatMoney(summary?.average_order_value, "USD") })}
                    icon={PointOfSaleRoundedIcon}
                    tone="blue"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <MetricCard
                    label={t("reports.unitsSold")}
                    value={Number(summary?.units_sold || 0).toLocaleString()}
                    helper={t("reports.returnedNet", { returned: Number(summary?.returned_units || 0).toLocaleString("en-US"), net: Number(summary?.net_units || 0).toLocaleString("en-US") })}
                    icon={ShoppingBasketRoundedIcon}
                    tone="violet"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <MetricCard
                    label={t(summary?.gross_profit !== null ? "reports.grossProfit" : "reports.refunds")}
                    value={summary?.gross_profit !== null
                      ? formatMoney(summary?.gross_profit, "USD")
                      : formatMoney(summary?.refunds, "USD")}
                    helper={summary?.gross_profit !== null
                      ? t("reports.refunded", { amount: formatMoney(summary?.refunds, "USD") })
                      : t("reports.costCoverage", { percent: Number(summary?.cost_coverage_percent || 0).toFixed(1) })}
                    icon={CompareArrowsRoundedIcon}
                    tone="amber"
                  />
                </Grid>
              </Grid>

              <Card sx={{ mt: 2.5, borderRadius: 4 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={900} mb={1}>{t("reports.profitBreakdown")}</Typography>
                  <Stack direction={{ xs: "column", md: "row" }} gap={2} justifyContent="space-between">
                    <Typography>{t("reports.netSalesValue", { amount: formatMoney(summary?.net_sales, "USD") })}</Typography>
                    <Typography>{t("reports.cogsValue", { amount: summary?.cogs == null ? t("reports.unknown") : formatMoney(summary.cogs, "USD") })}</Typography>
                    <Typography>{t("reports.grossProfitValue", { amount: summary?.gross_profit == null ? t("reports.unknown") : formatMoney(summary.gross_profit, "USD") })}</Typography>
                    <Typography>{t("reports.paidExpenses", { amount: formatMoney(summary?.operating_expenses || 0, "USD") })}</Typography>
                    <Typography fontWeight={900}>{t("reports.netProfitValue", { amount: summary?.net_profit == null ? t("reports.unknown") : formatMoney(summary.net_profit, "USD") })}</Typography>
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ mt: 2.5, borderRadius: 4 }}>
                <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1.5} sx={{ mb: 1 }}>
                    <Box>
                      <Typography variant="h6" fontWeight={900}>{t("reports.salesTrend")}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                        {t(period === "daily" ? "reports.dailyTrend" : "reports.monthlyTrend")}
                      </Typography>
                    </Box>
                    <Chip
                      icon={<InsightsRoundedIcon />}
                      label={t("reports.transactionsCount", { count: Number(summary?.transaction_count || 0).toLocaleString("en-US") })}
                      variant="outlined"
                    />
                  </Stack>
                  <SalesTrendChart points={summary?.trend || []} />
                </CardContent>
              </Card>

              <Grid container spacing={2.25} sx={{ mt: 0.25 }}>
                <Grid item xs={12} lg={6}>
                  <Card sx={{ height: "100%", borderRadius: 4 }}>
                    <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
                      <Typography variant="h6" fontWeight={900}>{t("reports.categorySales")}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, mb: 2 }}>
                        {t("reports.categoryHelp")}
                      </Typography>
                      <RankedBars
                        items={(summary?.category_totals || []).slice(0, 8)}
                        labelKey="category"
                        valueKey="net_revenue"
                        emptyLabel={t("reports.noCategory")}
                        valueFormatter={(value) => formatMoney(value, "USD")}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Card sx={{ height: "100%", borderRadius: 4 }}>
                    <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PaymentsRoundedIcon color="primary" />
                        <Typography variant="h6" fontWeight={900}>{t("reports.paymentMix")}</Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, mb: 2 }}>
                        {t("reports.paymentHelp")}
                      </Typography>
                      <PaymentBreakdown items={summary?.payment_totals || []} />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Grid container spacing={2.25} sx={{ mt: 0.25 }}>
                <Grid item xs={12} lg={7}>
                  <Card sx={{ height: "100%", borderRadius: 4, overflow: "hidden" }}>
                    <CardContent sx={{ p: 0 }}>
                      <Box sx={{ px: { xs: 2.25, md: 3 }, pt: 2.5, pb: 1.5 }}>
                        <Typography variant="h6" fontWeight={900}>{t("reports.topProducts")}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("reports.topProductsHelp")}
                        </Typography>
                      </Box>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{t("common.product")}</TableCell>
                              <TableCell align="right">{t("sales.sold")}</TableCell>
                              <TableCell align="right">{t("sales.returned")}</TableCell>
                              <TableCell align="right">{t("reports.netRevenue")}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(summary?.top_products || []).map((product) => (
                              <TableRow key={`${product.external_product_id || "name"}-${product.product_name}`}>
                                <TableCell><Typography fontWeight={800}>{product.product_name}</Typography></TableCell>
                                <TableCell align="right">{Number(product.quantity_sold).toLocaleString()}</TableCell>
                                <TableCell align="right">{Number(product.returned_quantity).toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800 }}>{formatMoney(product.net_revenue, "USD")}</TableCell>
                              </TableRow>
                            ))}
                            {(summary?.top_products || []).length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                  {t("reports.noProductLines")}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} lg={5}>
                  <Card sx={{ height: "100%", borderRadius: 4 }}>
                    <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
                      <Typography variant="h6" fontWeight={900}>{t("reports.sources")}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, mb: 2 }}>
                        {t("reports.sourcesHelp")}
                      </Typography>
                      <Stack spacing={1.5}>
                        {(summary?.source_totals || []).map((item) => (
                          <Box key={item.source} sx={{ p: 1.7, borderRadius: 3, bgcolor: "#f8fafc" }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                              <Box>
                                <Typography fontWeight={850}>
                                  {item.source === "native_pos" ? t("sales.nativePos") : item.source === "pos" ? t("sales.importedPos") : titleCase(item.source)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">{t("reports.transactionsCount", { count: item.transaction_count })}</Typography>
                              </Box>
                              <Typography fontWeight={900}>{formatMoney(item.net_sales, "USD")}</Typography>
                            </Stack>
                          </Box>
                        ))}
                        {(summary?.source_totals || []).length === 0 && <Typography color="text.secondary">{t("reports.noSources")}</Typography>}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Card sx={{ mt: 2.5, borderRadius: 4 }}>
                <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1.5}>
                    <Box>
                      <Typography variant="h6" fontWeight={900}>{t("reports.comparison")}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                        {t("reports.previousNetSales", { amount: formatMoney(summary?.comparison?.previous_net_sales, "USD") })}
                      </Typography>
                    </Box>
                    {comparisonChip}
                  </Stack>
                  <Alert severity="info" sx={{ mt: 2 }} icon={<InsightsRoundedIcon />}>
                    {t("reports.reportWindow", { from: formatDate(summary?.period_start), to: formatDate(summary?.period_end), timezone: summary?.timezone })}
                  </Alert>
                </CardContent>
              </Card>

              <AIInsightsCard
                period={period}
                anchorDate={anchorDate}
                transactionCount={Number(summary?.transaction_count || 0)}
              />
            </>
          )}
        </>
      )}
    </MainLayout>
  );
}
